// TODO: i18n — see Auth.js for pattern, replace hardcoded strings with t('namespace.key')
import { useState, useEffect } from 'react'
import { supabase } from '../supabase'

const CATEGORIES = [
  'Something not working',
  'Confusing flow',
  'Feature request',
  'Just feedback',
  'Urgent — please contact me',
]

export default function GetUnstuckButton() {
  const [isOpen, setIsOpen] = useState(false)
  const [category, setCategory] = useState('')
  const [description, setDescription] = useState('')
  const [screenshot, setScreenshot] = useState(null)
  const [screenshotPreview, setScreenshotPreview] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')
  const [hovered, setHovered] = useState(false)

  useEffect(() => {
    if (!submitted) return
    const timer = setTimeout(handleClose, 2500)
    return () => clearTimeout(timer)
  }, [submitted]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    setIsOpen(false)
    setTimeout(() => {
      setCategory('')
      setDescription('')
      setScreenshot(null)
      setScreenshotPreview(null)
      setSubmitted(false)
      setError('')
    }, 300)
  }

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) { setError('Screenshot must be under 5MB'); return }
    if (!file.type.startsWith('image/')) { setError('Please attach an image file'); return }
    setScreenshot(file)
    setError('')
    const reader = new FileReader()
    reader.onloadend = () => setScreenshotPreview(reader.result)
    reader.readAsDataURL(file)
  }

  const handleSubmit = async () => {
    if (!description.trim()) { setError('Please describe what happened'); return }
    if (!category) { setError('Please pick a category'); return }

    setSubmitting(true)
    setError('')

    try {
      const { data: { session } } = await supabase.auth.getSession()
      const userId = session?.user?.id || null
      const userEmail = session?.user?.email || null

      let reporterName = userEmail || 'Unknown'
      if (userId) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('first_name, last_name')
          .eq('id', userId)
          .maybeSingle()
        if (profile) {
          const fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
          if (fullName) reporterName = fullName
        }
      }

      let screenshotUrl = null
      if (screenshot) {
        const filename = `${userId || 'anon'}-${Date.now()}-${screenshot.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('bug-screenshots')
          .upload(filename, screenshot, { cacheControl: '3600', upsert: false })
        if (uploadError) {
          console.error('Screenshot upload failed:', uploadError)
        } else {
          const { data: urlData } = supabase.storage.from('bug-screenshots').getPublicUrl(uploadData.path)
          screenshotUrl = urlData.publicUrl
        }
      }

      const { error: insertError } = await supabase.from('bug_reports').insert({
        user_id: userId,
        reporter_name: reporterName,
        category,
        description: description.trim(),
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        screen_size: `${window.innerWidth}x${window.innerHeight}`,
        screenshot_url: screenshotUrl,
        status: 'new',
        is_public: false,
      })

      if (insertError) throw insertError
      setSubmitted(true)
    } catch (err) {
      console.error('Bug report submission failed:', err)
      setError("Couldn't send your report — please try again, or email kennethkrummes@gmail.com directly.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          ...S.floatingButton,
          transform: hovered ? 'scale(1.05)' : 'scale(1)',
          boxShadow: hovered
            ? '0 6px 24px rgba(44, 29, 20, 0.35)'
            : '0 4px 16px rgba(44, 29, 20, 0.25)',
        }}
        aria-label="Need help? Report an issue"
      >
        <span style={S.buttonIcon}>💬</span>
        <span style={S.buttonText}>Need help?</span>
      </button>

      {isOpen && (
        <div style={S.overlay} onClick={handleClose}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            {submitted ? (
              <div style={S.successState}>
                <div style={S.successIcon}>✓</div>
                <h2 style={S.successTitle}>Got it — thanks.</h2>
                <p style={S.successBody}>
                  Kenneth will take a look. You can keep using the app while we sort it out.
                </p>
              </div>
            ) : (
              <>
                <div style={S.modalHeader}>
                  <h2 style={S.modalTitle}>Tell us what's happening</h2>
                  <button onClick={handleClose} style={S.closeBtn} aria-label="Close">×</button>
                </div>

                <p style={S.subtitle}>
                  Even a quick note helps. We'll fix it fast — and you can keep using the app while we do.
                </p>

                <label style={S.label}>What kind of issue?</label>
                <select value={category} onChange={e => setCategory(e.target.value)} style={S.select} disabled={submitting}>
                  <option value="">Pick one…</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>

                <label style={S.label}>What were you trying to do?</label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Describe what happened. The more detail, the faster we can help."
                  style={S.textarea}
                  rows={5}
                  disabled={submitting}
                />

                <label style={S.label}>Screenshot (optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleScreenshotChange}
                  style={S.fileInput}
                  disabled={submitting}
                />
                {screenshotPreview && (
                  <div style={S.previewWrap}>
                    <img src={screenshotPreview} alt="Screenshot preview" style={S.preview} />
                    <button
                      onClick={() => { setScreenshot(null); setScreenshotPreview(null) }}
                      style={S.removePreview}
                      disabled={submitting}
                    >
                      Remove
                    </button>
                  </div>
                )}

                {error && <div style={S.errorBox}>{error}</div>}

                <div style={S.metaNote}>
                  We'll automatically include the page you're on, your browser info, and your account so we can recreate the issue.
                </div>

                <div style={S.buttonRow}>
                  <button onClick={handleClose} style={S.cancelBtn} disabled={submitting}>Cancel</button>
                  <button onClick={handleSubmit} style={S.submitBtn} disabled={submitting}>
                    {submitting ? 'Sending…' : 'Send report'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  )
}

const S = {
  floatingButton: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    zIndex: 100,
    background: '#2C1D14',
    color: '#FDFAF6',
    border: 'none',
    borderRadius: 28,
    padding: '12px 20px',
    fontSize: 14,
    fontWeight: 500,
    fontFamily: 'Calibri, sans-serif',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  },
  buttonIcon: { fontSize: 16 },
  buttonText: { letterSpacing: '0.01em' },
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(44, 29, 20, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 200,
    padding: 16,
    fontFamily: 'Calibri, sans-serif',
  },
  modal: {
    background: '#FDFAF6',
    borderRadius: 16,
    padding: 28,
    maxWidth: 480,
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 12px 40px rgba(44, 29, 20, 0.2)',
    border: '1px solid rgba(154, 123, 104, 0.15)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  modalTitle: {
    fontFamily: 'Georgia, serif',
    fontSize: 22,
    color: '#2C1D14',
    margin: 0,
    lineHeight: 1.3,
  },
  closeBtn: {
    background: 'transparent',
    border: 'none',
    fontSize: 24,
    color: '#9A7B68',
    cursor: 'pointer',
    padding: 0,
    lineHeight: 1,
    marginTop: -2,
  },
  subtitle: {
    fontSize: 14,
    color: '#9A7B68',
    margin: '0 0 24px 0',
    lineHeight: 1.5,
  },
  label: {
    display: 'block',
    fontSize: 13,
    color: '#2C1D14',
    fontWeight: 600,
    marginBottom: 6,
    marginTop: 16,
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    fontSize: 14,
    borderRadius: 8,
    border: '1px solid rgba(154, 123, 104, 0.3)',
    background: '#FFFFFF',
    color: '#2C1D14',
    fontFamily: 'Calibri, sans-serif',
    cursor: 'pointer',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    fontSize: 14,
    borderRadius: 8,
    border: '1px solid rgba(154, 123, 104, 0.3)',
    background: '#FFFFFF',
    color: '#2C1D14',
    fontFamily: 'Calibri, sans-serif',
    resize: 'vertical',
    minHeight: 100,
    boxSizing: 'border-box',
  },
  fileInput: {
    fontSize: 13,
    color: '#9A7B68',
    fontFamily: 'Calibri, sans-serif',
  },
  previewWrap: {
    marginTop: 8,
    display: 'inline-block',
  },
  preview: {
    maxWidth: '100%',
    maxHeight: 160,
    borderRadius: 8,
    border: '1px solid rgba(154, 123, 104, 0.3)',
    display: 'block',
  },
  removePreview: {
    marginTop: 6,
    background: 'transparent',
    border: 'none',
    color: '#B8721F',
    fontSize: 12,
    cursor: 'pointer',
    padding: 0,
    textDecoration: 'underline',
  },
  errorBox: {
    marginTop: 16,
    padding: 12,
    background: '#FFF4E6',
    border: '1px solid #B8721F',
    borderRadius: 8,
    fontSize: 13,
    color: '#2C1D14',
  },
  metaNote: {
    marginTop: 16,
    fontSize: 12,
    color: '#9A7B68',
    fontStyle: 'italic',
    lineHeight: 1.5,
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 24,
  },
  cancelBtn: {
    background: 'transparent',
    color: '#9A7B68',
    border: 'none',
    padding: '10px 16px',
    fontSize: 14,
    cursor: 'pointer',
    fontFamily: 'Calibri, sans-serif',
  },
  submitBtn: {
    background: '#B8721F',
    color: '#FDFAF6',
    border: 'none',
    borderRadius: 8,
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'Calibri, sans-serif',
    minWidth: 120,
  },
  successState: {
    textAlign: 'center',
    padding: '20px 0',
  },
  successIcon: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: '#5C7A4F',
    color: '#FDFAF6',
    fontSize: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px auto',
    fontWeight: 'bold',
  },
  successTitle: {
    fontFamily: 'Georgia, serif',
    fontSize: 22,
    color: '#2C1D14',
    margin: '0 0 8px 0',
  },
  successBody: {
    fontSize: 14,
    color: '#9A7B68',
    margin: 0,
    lineHeight: 1.5,
  },
}
